import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MobileMetricCard } from '../components/dashboard/MobileMetricCard'
import { MobileChartCard } from '../components/dashboard/MobileChartCard'
import { apiClient } from '../services/api'
import { QUERY_KEYS } from '../lib/react-query'
import { useNodes, useNodeStats } from '../hooks/useNodes'
import { formatNumber, formatUptime, formatRelativeTime } from '../utils/format'
import { 
  Activity,
  Server,
  Network,
  Shield,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Users,
  Cpu,
  HardDrive,
  Zap,
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from 'recharts';

export const MobileDashboard: React.FC = React.memo(() => {
  const { data: nodes = [], isLoading: nodesLoading } = useNodes();
  const nodeStats = useNodeStats();
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['metrics']));

  const { data: dashboardMetrics, isLoading: metricsLoading } = useQuery({
    queryKey: QUERY_KEYS.dashboardMetrics(),
    queryFn: async () => {
      const response = await apiClient.getDashboardMetrics();
      if (!response.success) {
        throw new Error(response.error);
      }
      return response.data;
    },
    refetchInterval: 60000, // Less frequent on mobile
  });

  const { data: alerts = [] } = useQuery({
    queryKey: QUERY_KEYS.allAlerts(),
    queryFn: async () => {
      const response = await apiClient.getAlerts({ acknowledged: false });
      if (!response.success) {
        throw new Error(response.error);
      }
      return response.data || [];
    },
    refetchInterval: 60000,
  });

  // Generate performance data
  const performanceData = React.useMemo(() => {
    const baseTime = Date.now();
    return Array.from({ length: 8 }, (_, i) => { // Less data points for mobile
      const time = new Date(baseTime - (7 - i) * 10 * 60 * 1000).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      });
      return {
        time,
        cpu: Math.min(95, 40 + Math.random() * 30 + (nodeStats.total > 10 ? 10 : 0)),
        memory: Math.min(90, 55 + Math.random() * 25 + (nodeStats.total > 10 ? 5 : 0)),
        network: Math.min(1000, nodeStats.total * (8 + Math.random() * 4)),
      };
    });
  }, [nodeStats.total]);

  const nodeStatusData = [
    { name: 'Healthy', value: nodeStats.healthy, color: 'var(--status-online)' },
    { name: 'Degraded', value: nodeStats.degraded, color: 'var(--status-degraded)' },
    { name: 'Offline', value: nodeStats.offline, color: 'var(--status-offline)' },
  ];

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  if (nodesLoading || metricsLoading) {
    return (
      <div className="p-4 space-y-4">
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="skeleton h-32 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Mobile Header */}
      <div className="sticky top-0 z-40 bg-gray-900/95 backdrop-blur-sm border-b border-gray-800">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-white">Dashboard</h1>
              <p className="text-xs text-gray-400">System Overview</p>
            </div>
            <button className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800 transition-colors">
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Primary Metrics Section */}
        <div className="space-y-3">
          <MobileMetricCard
            title="Total Nodes"
            value={nodeStats.total}
            icon={Server}
            subtitle={`${nodeStats.healthy} healthy`}
            trend={{
              value: 12,
              direction: 'up',
              label: 'Compared to last week'
            }}
            details={[
              { label: 'Healthy', value: nodeStats.healthy, color: 'var(--status-online)' },
              { label: 'Degraded', value: nodeStats.degraded, color: 'var(--status-degraded)' },
              { label: 'Offline', value: nodeStats.offline, color: 'var(--status-offline)' },
            ]}
            status="success"
          />

          <MobileMetricCard
            title="Health Score"
            value={`${nodeStats.healthPercentage}%`}
            icon={Activity}
            subtitle="System performance"
            trend={{
              value: 3,
              direction: 'up',
              label: '3% improvement from yesterday'
            }}
            details={[
              { label: 'CPU Average', value: '45%' },
              { label: 'Memory Usage', value: '62%' },
              { label: 'Disk I/O', value: 'Normal' },
              { label: 'Network Latency', value: '12ms' },
            ]}
            status={nodeStats.healthPercentage >= 90 ? 'success' : 'warning'}
          />

          <MobileMetricCard
            title="Active Alerts"
            value={alerts.length}
            icon={AlertTriangle}
            subtitle={alerts.length > 0 ? `${alerts.filter(a => a.severity === 'critical').length} critical` : 'All clear'}
            status={alerts.length === 0 ? 'success' : alerts.some(a => a.severity === 'critical') ? 'error' : 'warning'}
            details={alerts.slice(0, 5).map(alert => ({
              label: alert.title || 'Alert',
              value: alert.severity,
              color: alert.severity === 'critical' ? '#ef4444' : alert.severity === 'warning' ? '#f59e0b' : '#3b82f6'
            }))}
          />

          <MobileMetricCard
            title="Network Throughput"
            value={`${formatNumber(dashboardMetrics?.performance?.networkThroughput || nodeStats.total * 83)}/s`}
            icon={Network}
            subtitle={`${Math.round(nodeStats.total * 8.3)} MB/s avg`}
            trend={{
              value: 18,
              direction: 'up',
              label: 'Network utilization increasing'
            }}
            details={[
              { label: 'Inbound', value: `${Math.round(nodeStats.total * 4.2)} MB/s` },
              { label: 'Outbound', value: `${Math.round(nodeStats.total * 4.1)} MB/s` },
              { label: 'Packet Loss', value: '0.01%', color: '#22c55e' },
              { label: 'Active Connections', value: formatNumber(nodeStats.total * 127) },
            ]}
          />
        </div>

        {/* Charts Section - Collapsible */}
        <div className="space-y-3">
          <MobileChartCard
            title="System Performance"
            subtitle="Real-time metrics"
            defaultExpanded={false}
            summary={[
              { label: 'CPU', value: '45%', trend: 'up' },
              { label: 'Memory', value: '62%', trend: 'neutral' },
              { label: 'Network', value: '380 MB/s', trend: 'up' },
              { label: 'Uptime', value: '99.9%', trend: 'neutral' },
            ]}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={performanceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="time" 
                  stroke="#9ca3af" 
                  fontSize={11}
                  tick={{ fill: '#9ca3af' }}
                />
                <YAxis 
                  stroke="#9ca3af" 
                  fontSize={11}
                  tick={{ fill: '#9ca3af' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1f2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="cpu" 
                  stroke="#3b82f6" 
                  strokeWidth={2} 
                  dot={false} 
                  name="CPU %"
                />
                <Line 
                  type="monotone" 
                  dataKey="memory" 
                  stroke="#22c55e" 
                  strokeWidth={2} 
                  dot={false}
                  name="Memory %"
                />
              </LineChart>
            </ResponsiveContainer>
          </MobileChartCard>

          <MobileChartCard
            title="Node Distribution"
            subtitle="Current status breakdown"
            defaultExpanded={false}
            summary={[
              { label: 'Total Nodes', value: nodeStats.total },
              { label: 'Availability', value: `${Math.round((nodeStats.healthy / nodeStats.total) * 100)}%` },
            ]}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={nodeStatusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {nodeStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1f2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </MobileChartCard>
        </div>

        {/* Quick Actions */}
        <div className="mt-6">
          <h2 className="text-lg font-semibold text-white mb-3">Quick Actions</h2>
          <div className="grid grid-cols-2 gap-3">
            <button className="p-4 bg-gray-800/50 hover:bg-gray-800 border border-gray-700 rounded-xl text-center transition-colors">
              <Shield className="w-6 h-6 text-blue-500 mx-auto mb-2" />
              <span className="text-sm text-gray-300">Security Scan</span>
            </button>
            <button className="p-4 bg-gray-800/50 hover:bg-gray-800 border border-gray-700 rounded-xl text-center transition-colors">
              <Users className="w-6 h-6 text-green-500 mx-auto mb-2" />
              <span className="text-sm text-gray-300">Add Node</span>
            </button>
            <button className="p-4 bg-gray-800/50 hover:bg-gray-800 border border-gray-700 rounded-xl text-center transition-colors">
              <Cpu className="w-6 h-6 text-purple-500 mx-auto mb-2" />
              <span className="text-sm text-gray-300">Performance</span>
            </button>
            <button className="p-4 bg-gray-800/50 hover:bg-gray-800 border border-gray-700 rounded-xl text-center transition-colors">
              <HardDrive className="w-6 h-6 text-orange-500 mx-auto mb-2" />
              <span className="text-sm text-gray-300">Storage</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});